import React, { useRef, useState, useMemo } from 'react';
import { Select } from 'antd';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import annotationPlugin from 'chartjs-plugin-annotation';
import zoomPlugin from 'chartjs-plugin-zoom';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  annotationPlugin,
  zoomPlugin
);

export type MarketCategory = 'all' | 'forex' | 'metals' | 'commodities' | 'indices' | 'crypto' | 'stocks';

export const MARKET_DEFINITIONS: { value: MarketCategory; label: string }[] = [
  { value: 'all', label: 'Tất cả thị trường' },
  { value: 'forex', label: 'Ngoại hối (Forex)' },
  { value: 'metals', label: 'Kim loại quý (Metals)' },
  { value: 'commodities', label: 'Hàng hóa / Năng lượng' },
  { value: 'indices', label: 'Chỉ số (Indices)' },
  { value: 'crypto', label: 'Tiền điện tử (Crypto)' },
  { value: 'stocks', label: 'Cổ phiếu (Stocks)' },
];

/**
 * Phân loại mã giao dịch (symbol) về nhóm thị trường tương ứng
 */
export function getMarketCategory(rawSymbol: string): MarketCategory {
  if (!rawSymbol) return 'forex';
  const s = rawSymbol.toUpperCase().replace(/[^A-Z0-9]/g, '');

  // 1. Kim loại quý (Metals)
  if (
    s.startsWith('XAU') ||
    s.includes('GOLD') ||
    s.startsWith('XAG') ||
    s.includes('SILVER') ||
    s.startsWith('XPT') ||
    s.startsWith('XPD') ||
    s.includes('PLATINUM') ||
    s.includes('PALLADIUM')
  ) {
    return 'metals';
  }

  // 2. Năng lượng & Hàng hóa (Commodities)
  if (
    s.includes('OIL') ||
    s.includes('WTI') ||
    s.includes('BRENT') ||
    s.includes('NGAS') ||
    s.includes('NATGAS') ||
    s.includes('GAS') ||
    s.includes('COPPER') ||
    s.includes('USO') ||
    s.includes('UKO')
  ) {
    return 'commodities';
  }

  // 3. Tiền điện tử (Crypto)
  const cryptoPrefixes = ['BTC', 'ETH', 'SOL', 'XRP', 'BNB', 'DOGE', 'ADA', 'DOT', 'LTC', 'BCH', 'AVAX', 'LINK', 'NEAR', 'MATIC', 'SHIB', 'TRX', 'UNI'];
  if (cryptoPrefixes.some((cp) => s.startsWith(cp)) || s.endsWith('USDT') || s.endsWith('USDC')) {
    return 'crypto';
  }

  // 4. Chỉ số chứng khoán (Indices)
  const indexKeywords = [
    'US30', 'DJ30', 'WS30', 'DOW',
    'NAS100', 'USTEC', 'NDX', 'TECH100', 'NAS',
    'SPX500', 'US500', 'SP500', 'SPX',
    'GER30', 'GER40', 'DAX', 'DE30', 'DE40',
    'UK100', 'FTSE', 'UKX',
    'JP225', 'NIKKEI', 'N225',
    'HK50', 'HSI',
    'AUS200', 'AU200', 'ASX200',
    'EU50', 'STOXX50', 'EUSTX50',
    'FRA40', 'CAC40'
  ];
  if (indexKeywords.some((ik) => s.includes(ik))) {
    return 'indices';
  }

  // 5. Cổ phiếu (Stocks)
  const stockSymbols = ['AAPL', 'TSLA', 'NVDA', 'MSFT', 'AMZN', 'GOOG', 'META', 'NFLX', 'BABA', 'AMD', 'INTC', 'DIS'];
  if (stockSymbols.some((st) => s.includes(st))) {
    return 'stocks';
  }

  // 6. Mặc định là Forex (Ngoại tệ)
  return 'forex';
}

export interface MainAccountBalanceChartProps {
  data?: number[];
  labels?: string[];
  chartPoints?: {
    balance: number;
    targetBalance: number;
    maxloss: number;
    balanceSize: number;
  };
  trades?: any[]; // Danh sách lệnh đóng (journal_closed_trades hoặc RawTrade[])
  selectedMarket?: string;
  showMarketSelector?: boolean; // Tạm thời ẩn, mặc định false
  onMarketChange?: (market: string) => void;
  onZoomChange?: (zoomLevel: number) => void;
  onPanChange?: (panOffset: number) => void;
  onResetZoom?: () => void;
  onResetPan?: () => void;
}

// Component Overlay hiển thị thông báo "Không có dữ liệu"
const EmptyDataOverlay: React.FC<{ show: boolean; message?: string }> = ({
  show,
  message = 'Không có dữ liệu',
}) => {
  if (!show) return null;
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-[1px] z-10 pointer-events-none">
      <span className="text-xs sm:text-sm font-medium text-slate-500 bg-white/95 border border-slate-200 px-3.5 py-2 rounded-lg shadow-sm">
        {message}
      </span>
    </div>
  );
};

const MainAccountBalanceChart: React.FC<MainAccountBalanceChartProps> = ({ 
  data = [],
  labels = [],
  chartPoints = {
    balance: 0,
    targetBalance: 0,
    maxloss: 0,
    balanceSize: 0,
  },
  trades = [],
  selectedMarket: controlledMarket,
  showMarketSelector = false,
  onMarketChange,
}) => {
  const chartRef = useRef<any>(null);
  const xRangeRef = useRef<{ min?: number; max?: number }>({});

  // Quản lý state thị trường được chọn (nếu có controlled prop thì dùng, không thì tự quản lý)
  const [internalMarket, setInternalMarket] = useState<string>('all');
  const activeMarket = controlledMarket !== undefined ? controlledMarket : internalMarket;

  const handleMarketChange = (val: string) => {
    if (controlledMarket === undefined) {
      setInternalMarket(val);
    }
    if (onMarketChange) {
      onMarketChange(val);
    }
  };

  // Đếm số lệnh và phân loại cho từng thị trường từ danh sách trades
  const { marketStats, marketOptions } = useMemo(() => {
    const stats: Record<string, { count: number; profit: number; wins: number }> = {
      all: { count: trades.length, profit: 0, wins: 0 },
      forex: { count: 0, profit: 0, wins: 0 },
      metals: { count: 0, profit: 0, wins: 0 },
      commodities: { count: 0, profit: 0, wins: 0 },
      indices: { count: 0, profit: 0, wins: 0 },
      crypto: { count: 0, profit: 0, wins: 0 },
      stocks: { count: 0, profit: 0, wins: 0 },
    };

    trades.forEach((t) => {
      const sym = t?.symbol || t?.pair || t?.Symbol || '';
      const cat = getMarketCategory(sym);
      const profit = Number(t?.net_profit ?? t?.profit ?? 0);
      const isWin = profit > 0;

      stats.all.profit += profit;
      if (isWin) stats.all.wins += 1;

      if (stats[cat]) {
        stats[cat].count += 1;
        stats[cat].profit += profit;
        if (isWin) stats[cat].wins += 1;
      }
    });

    const opts = MARKET_DEFINITIONS.map((def) => {
      const s = stats[def.value];
      const countText = s && s.count > 0 ? ` (${s.count} lệnh)` : '';
      return {
        value: def.value,
        label: `${def.label}${countText}`,
      };
    });

    return { marketStats: stats, marketOptions: opts };
  }, [trades]);

  // Tính toán dữ liệu biểu đồ cho riêng loại thị trường đang được chọn (mỗi biểu đồ chỉ hiển thị 1 loại)
  const { activeData, activeLabels, activeChartPoints, currentStats } = useMemo(() => {
    const baseBalance = chartPoints.balanceSize || chartPoints.balance || 0;

    // Trường hợp hiển thị "Tất cả thị trường"
    if (activeMarket === 'all' || !trades || trades.length === 0) {
      return {
        activeData: data,
        activeLabels: labels,
        activeChartPoints: chartPoints,
        currentStats: marketStats['all'],
      };
    }

    // Lọc các lệnh thuộc loại thị trường được chọn
    const filteredTrades = trades.filter((t) => {
      const sym = t?.symbol || t?.pair || t?.Symbol || '';
      return getMarketCategory(sym) === activeMarket;
    });

    if (filteredTrades.length === 0) {
      return {
        activeData: [],
        activeLabels: [],
        activeChartPoints: chartPoints,
        currentStats: marketStats[activeMarket],
      };
    }

    // Sắp xếp các lệnh theo thứ tự thời gian tăng dần
    const sortedTrades = [...filteredTrades].sort((a, b) => {
      const timeA = new Date(a.close_time || a.open_time || 0).getTime();
      const timeB = new Date(b.close_time || b.open_time || 0).getTime();
      return timeA - timeB;
    });

    let runningBalance = baseBalance;
    const computedData: number[] = [baseBalance];
    const computedLabels: string[] = ['Bắt đầu'];

    sortedTrades.forEach((t, idx) => {
      const p = Number(t?.net_profit ?? t?.profit ?? 0);
      runningBalance += p;
      computedData.push(Math.round(runningBalance * 10000) / 10000);
      const timeStr = t?.close_time || t?.open_time;
      const label = timeStr
        ? new Date(timeStr).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })
        : `#${idx + 1}`;
      computedLabels.push(label);
    });

    const computedPoints = {
      balance: runningBalance,
      targetBalance: chartPoints.targetBalance || baseBalance * 1.05,
      maxloss: chartPoints.maxloss || baseBalance * 0.9,
      balanceSize: baseBalance,
    };

    return {
      activeData: computedData,
      activeLabels: computedLabels,
      activeChartPoints: computedPoints,
      currentStats: marketStats[activeMarket],
    };
  }, [activeMarket, trades, data, labels, chartPoints, marketStats]);

  // Kiểm tra điều kiện mảng dữ liệu rỗng
  const isEmptyData = !activeData || activeData.length === 0;

  // Cung cấp dữ liệu fallback khi rỗng
  const safeData = isEmptyData ? [0] : activeData;
  const safeLabels = isEmptyData ? [''] : activeLabels;

  // Hàm tính tỉ lệ vị trí ngưỡng (Threshold) trên trục Y từ 0 đến 1
  const getThresholdStop = (chart: any, threshold: number) => {
    const { chartArea, scales } = chart;
    if (!chartArea || !scales.y) return 0.5;
    const yPixel = scales.y.getPixelForValue(threshold);
    const height = chartArea.bottom - chartArea.top;
    if (height <= 0) return 0.5;
    const stop = (yPixel - chartArea.top) / height;
    return Math.max(0, Math.min(1, stop));
  };

  const chartData = {
    labels: safeLabels,
    datasets: [
      {
        label: 'Vốn chủ sở hữu',
        data: safeData.map((val) => Math.round(val * 10000) / 10000),
        borderColor: (context: any) => {
          if (isEmptyData) return 'transparent';
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) return '#14b8a6';

          const stop = getThresholdStop(chart, activeChartPoints.balanceSize);
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);

          gradient.addColorStop(0, '#14b8a6');
          gradient.addColorStop(stop, '#14b8a6');
          gradient.addColorStop(stop, '#f43f5e');
          gradient.addColorStop(1, '#f43f5e');

          return gradient;
        },
        backgroundColor: (context: any) => {
          if (isEmptyData) return 'transparent';
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) return null;

          const stop = getThresholdStop(chart, activeChartPoints.balanceSize);
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);

          gradient.addColorStop(0, 'rgba(20, 184, 166, 0.35)');
          gradient.addColorStop(stop, 'rgba(20, 184, 166, 0.02)');
          gradient.addColorStop(stop, 'rgba(244, 63, 94, 0.02)');
          gradient.addColorStop(1, 'rgba(244, 63, 94, 0.35)');

          return gradient;
        },
        borderWidth: isEmptyData ? 0 : 2,
        fill: !isEmptyData,
        tension: 0,
        pointRadius: 0,
        pointHoverRadius: isEmptyData ? 0 : 4,
      },
    ],
  };

  const yMinRaw = isEmptyData ? 0 : Math.min(...activeData);
  const yMaxRaw = isEmptyData ? 100 : Math.max(...activeData);
  const yMin = yMinRaw === yMaxRaw ? yMinRaw * 0.99 : yMinRaw;
  const yMax = yMinRaw === yMaxRaw ? yMaxRaw * 1.01 : yMaxRaw;

  const options = {
    animation: {
      duration: 0,
    },
    animations: {
      colors: {
        properties: ['backgroundColor', 'borderColor'],
        type: 'color',
        duration: 150,
      },
      x: { duration: 0 },
      y: { duration: 0 },
    },
    transitions: {
      active: {
        animation: { duration: 150 },
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    events: isEmptyData ? [] : ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'],
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: !isEmptyData,
        backgroundColor: 'rgba(15, 23, 42, 0.9)',
        titleFont: { family: 'Inter' },
        bodyFont: { family: 'JetBrains Mono' },
        padding: 12,
        cornerRadius: 6,
      },
      zoom: {
        zoom: {
          wheel: { enabled: !isEmptyData },
          pinch: { enabled: !isEmptyData },
          mode: 'x' as const,
          onZoom: ({ chart }: any) => {
            const xScale = chart.scales.x;
            if (xScale) {
              xRangeRef.current = { min: xScale.min, max: xScale.max };
            }
          },
        },
        pan: {
          enabled: !isEmptyData,
          mode: 'x' as const,
          onPan: ({ chart }: any) => {
            const xScale = chart.scales.x;
            if (xScale) {
              xRangeRef.current = { min: xScale.min, max: xScale.max };
            }
          },
        },
      },
      annotation: {
        annotations: isEmptyData
          ? {}
          : {
              line1: {
                type: 'line',
                yMin: activeChartPoints.balance,
                yMax: activeChartPoints.balance,
                borderColor: '#3b82f6',
                borderWidth: 1,
                borderDash: [4, 4],
                label: {
                  display: true,
                  content: [activeChartPoints.balance.toLocaleString(), 'Số dư hiện tại'],
                  position: 'start',
                  backgroundColor: '#3b82f6',
                  color: '#fff',
                  font: [{ size: 10, weight: 'bold' as const }, { size: 8 }],
                  padding: 4,
                },
              },
              line2: {
                type: 'line',
                yMin: activeChartPoints.targetBalance,
                yMax: activeChartPoints.targetBalance,
                borderColor: '#14b8a6',
                borderWidth: 1,
                label: {
                  display: true,
                  content: [activeChartPoints.targetBalance.toLocaleString(), 'Mục tiêu lợi nhuận'],
                  position: 'start',
                  backgroundColor: '#14b8a6',
                  color: '#fff',
                  font: [{ size: 10, weight: 'bold' as const }, { size: 8 }],
                  padding: 4,
                },
              },
              line3: {
                type: 'line',
                yMin: activeChartPoints.balanceSize,
                yMax: activeChartPoints.balanceSize,
                borderColor: '#64748b',
                borderWidth: 1,
                borderDash: [4, 4],
                label: {
                  display: true,
                  content: [activeChartPoints.balanceSize.toLocaleString(), 'Số dư ban đầu'],
                  position: 'start',
                  backgroundColor: '#64748b',
                  color: '#fff',
                  font: [{ size: 10, weight: 'bold' as const }, { size: 8 }],
                  padding: 4,
                },
              },
              line4: {
                type: 'line',
                yMin: activeChartPoints.maxloss,
                yMax: activeChartPoints.maxloss,
                borderColor: '#f43f5e',
                borderWidth: 1,
                label: {
                  display: true,
                  content: [activeChartPoints.maxloss.toLocaleString(), 'Giới hạn sụt giảm'],
                  position: 'start',
                  backgroundColor: '#f43f5e',
                  color: '#fff',
                  font: [{ size: 10, weight: 'bold' as const }, { size: 8 }],
                  padding: 4,
                },
              },
            },
      },
    },
    scales: {
      x: {
        min: xRangeRef.current.min,
        max: xRangeRef.current.max,
        grid: { display: false },
        ticks: {
          display: !isEmptyData,
          font: { family: 'JetBrains Mono', size: 10 },
          color: '#64748b',
          padding: 8,
          autoSkip: true,
          maxTicksLimit: 10,
          maxRotation: 0,
        },
      },
      y: {
        position: 'left' as const,
        grid: { color: '#f1f5f9' },
        min: yMin,
        max: yMax,
        ticks: {
          display: !isEmptyData,
          font: { family: 'JetBrains Mono', size: 10 },
          color: '#64748b',
          callback: (value: any) => '$' + Number((value / 1000).toFixed(4)) + 'k',
          padding: 8,
        },
      },
    },
    layout: {
      padding: { left: 0, right: 0 },
    },
  };

  const winRate =
    currentStats && currentStats.count > 0
      ? Math.round((currentStats.wins / currentStats.count) * 100)
      : 0;

  return (
    <div className={`relative w-full h-full ${showMarketSelector ? 'flex flex-col' : ''} group`}>
      {/* Header Toolbar: Chọn loại thị trường giao dịch (Tạm ẩn, mặc định hiển thị tất cả) */}
      {showMarketSelector && (
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2.5 z-20 flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
              Thị trường:
            </span>
            <Select
              size="small"
              value={activeMarket}
              onChange={handleMarketChange}
              style={{ width: 195 }}
              className="text-xs font-medium !shadow-none"
              options={marketOptions}
            />
          </div>

          {/* Thông tin thống kê nhanh của loại thị trường đang chọn */}
          {currentStats && currentStats.count > 0 && (
            <div className="flex items-center gap-3 text-xs bg-slate-50/80 px-2.5 py-1 rounded-md border border-slate-100">
              <span className="text-slate-500">
                Lệnh: <strong className="text-slate-800 font-mono">{currentStats.count}</strong>
              </span>
              <span className="text-slate-500">
                Thắng: <strong className="text-slate-800 font-mono">{winRate}%</strong>
              </span>
              <span className="text-slate-500">
                P&L:{' '}
                <strong
                  className={`font-mono ${
                    currentStats.profit >= 0 ? '!text-emerald-600' : '!text-rose-600'
                  }`}
                >
                  {currentStats.profit >= 0 ? '+' : ''}$
                  {currentStats.profit.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </strong>
              </span>
            </div>
          )}
        </div>
      )}

      {/* Vùng vẽ biểu đồ */}
      <div className={`relative ${showMarketSelector ? 'flex-1 min-h-0' : 'h-full'} w-full`}>
        <Line ref={chartRef} data={chartData} options={options as any} />
        <EmptyDataOverlay
          show={isEmptyData}
          message={
            activeMarket !== 'all'
              ? 'Không có dữ liệu cho loại thị trường này'
              : 'Không có dữ liệu'
          }
        />
      </div>
    </div>
  );
};

export default MainAccountBalanceChart;